import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Wand2, RefreshCw, Heart, ShieldCheck, ArrowLeft, ImagePlus, Lock } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Stardust } from "@/components/Stardust";
import { PackageCard } from "@/components/PackageCard";
import { http, apiError } from "@/lib/api";
import { CharacterBuilder } from "@/components/CharacterBuilder";
import { DEFAULTS, PET_DEFAULT_TYPE, PET_CREATURE_TYPES } from "@/lib/builderOptions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const WIZARD_STEPS = ["Upload", "Character Details", "Preview", "Download"];
const STAGE_TO_STEP = { upload: 0, "screen-fail": 0, "sketch-review": 0, questionnaire: 1, generating: 1, preview: 2 };

const MEDIA_OPTIONS = [
  { id: "drawing", label: "Drawing", thumb: "https://static.prod-images.emergentagent.com/jobs/f577895a-fbfb-472f-bfd1-813d30680340/images/13bf84ebb6423287bb89d64d92f2afd47475841f306ae2b4b95886e978c01603.jpeg" },
  { id: "color_pencil", label: "Color Pencil Drawing", thumb: "https://static.prod-images.emergentagent.com/jobs/f577895a-fbfb-472f-bfd1-813d30680340/images/c8e8fc45b0de6ae6894da9fcf1776b0a4bc8ca41378f7d648fdef91c9c311ed0.jpeg" },
  { id: "painting", label: "Painting", thumb: "https://static.prod-images.emergentagent.com/jobs/f577895a-fbfb-472f-bfd1-813d30680340/images/cc1560a7441f439352e6e0d6bad4689ba54a864494f2494ab9dd14203c766e20.jpeg" },
  { id: "sketch", label: "Sketch", thumb: "https://static.prod-images.emergentagent.com/jobs/f577895a-fbfb-472f-bfd1-813d30680340/images/13ab0ad55d66401bae7b9129534f37f1f45b49f27d82ef81fe8cf29a4901554a.jpeg" },
];
const MEDIA_LABELS = MEDIA_OPTIONS.reduce((a, m) => ({ ...a, [m.id]: m.label.toLowerCase() }), {});

const PERSON_TRAIT_FIELDS = [
  { key: "age_group", label: "Age group" },
  { key: "gender", label: "Gender" },
  { key: "hair_color", label: "Hair color" },
  { key: "eye_color", label: "Eye color" },
  { key: "facial_features", label: "Facial features" },
];
const ANIMAL_TRAIT_FIELDS = [
  { key: "species", label: "Species" },
  { key: "breed", label: "Breed" },
  { key: "notable_features", label: "Notable features" },
];


export default function Create() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const fileRef = useRef();
  const [stage, setStage] = useState("upload");
  const [cfg, setCfg] = useState({ packages: [], preview_cap: 5, trust_download: "", trust_drawings: "" });
  const [creationId, setCreationId] = useState(null);
  const [failReason, setFailReason] = useState("");
  const [q, setQ] = useState(DEFAULTS);
  const [generations, setGenerations] = useState([]);
  const [used, setUsed] = useState(0);
  const [remaining, setRemaining] = useState(5);
  const [selectedGen, setSelectedGen] = useState(null);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [convertible, setConvertible] = useState(false);
  const [converting, setConverting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [sketchPreview, setSketchPreview] = useState(null);
  const [sketchHint, setSketchHint] = useState("");
  const [medium, setMedium] = useState("sketch");
  const [traits, setTraits] = useState(null);
  const [subjectKind, setSubjectKind] = useState("person");
  const [rendersLeft, setRendersLeft] = useState(null);
  const [cooldown, setCooldown] = useState(false);

  useEffect(() => {
    http.get("/auth/me").then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
  }, []);

  useEffect(() => {
    http.get("/config").then((r) => {
      setCfg(r.data);
      setRemaining(r.data.preview_cap);
      const pkgId = searchParams.get("pkg");
      if (pkgId) {
        const match = (r.data.packages || []).find((p) => p.id === pkgId);
        if (match) setSelectedPkg(match);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG or JPEG).");
      return;
    }
    setUploadPreview(URL.createObjectURL(file));
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await http.post("/creations/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCreationId(data.creation_id);
      if (data.passed) {
        toast.success("Beautiful drawing! Let's bring it to life.");
        setStage("questionnaire");
      } else {
        setFailReason(data.reason || "This doesn't look like an original drawing.");
        setConvertible(!!data.convertible);
        setStage("screen-fail");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function convertToSketch(hint = "", editedTraits = null) {
    setConverting(true);
    try {
      const { data } = await http.post(`/creations/${creationId}/convert-to-sketch`, {
        consent: true,
        medium,
        hint,
        traits: editedTraits || undefined,
      });
      setSketchPreview(`data:image/png;base64,${data.sketch_b64}`);
      setUploadPreview(`data:image/png;base64,${data.sketch_b64}`);
      setTraits(data.traits || {});
      setSubjectKind(data.kind || "person");
      if (typeof data.renders_left === "number") setRendersLeft(data.renders_left);
      // brief debounce so rapid taps can't spend credits
      setCooldown(true);
      setTimeout(() => setCooldown(false), 3000);
      if (data.kind === "animal") {
        setQ((prev) => ({
          ...prev,
          type: PET_CREATURE_TYPES.includes(prev.type) ? prev.type : PET_DEFAULT_TYPE,
        }));
      }
      setConvertible(false);
      setStage("sketch-review");
      toast.success(`Here's your ${MEDIA_LABELS[data.medium || medium]} — review it before we build your character.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setConverting(false);
    }
  }

  async function submitQuestionnaire() {
    if (!q.name || !q.type || !q.visual_style) {
      toast.error("Please complete the character name, type and image style.");
      return;
    }
    setStage("generating");
    setBusy(true);
    try {
      const { data } = await http.post(`/creations/${creationId}/generate`, q);
      setGenerations((prev) => [{ ...data, id: data.generation_id }, ...prev]);
      setUsed(data.previews_used);
      setRemaining(data.remaining);
      setSelectedGen(data.generation_id);
      setStage("preview");
      toast.success("Your legend has appeared!");
    } catch (e) {
      toast.error(apiError(e));
      setStage(generations.length ? "preview" : "questionnaire");
    } finally {
      setBusy(false);
    }
  }

  function tryAgain() {
    if (remaining <= 0) {
      toast.error(`You've used all ${cfg.preview_cap} free previews. Download one, or start a new session.`);
      return;
    }
    setStage("questionnaire");
  }

  async function pay() {
    if (!selectedPkg) {
      toast.error("Pick a pack to unlock your download.");
      return;
    }
    if (isAdmin) {
      setBusy(true);
      try {
        const { data } = await http.post("/admin/studio/unlock", {
          creation_id: creationId,
          generation_id: selectedGen,
          package_id: selectedPkg.id,
        });
        toast.success("Unlocked free (admin mode).");
        nav(`/download/${data.download_token}`);
      } catch (e) {
        toast.error(apiError(e));
        setBusy(false);
      }
      return;
    }
    if (!/.+@.+\..+/.test(email)) {
      toast.error("Enter a valid email for your download link.");
      return;
    }
    if (!rightsConfirmed) {
      toast.error("Please confirm you have the rights to use this image before checkout.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await http.post("/payments/checkout", {
        creation_id: creationId,
        generation_id: selectedGen,
        package_id: selectedPkg.id,
        email,
        origin_url: window.location.origin,
        rights_confirmed: true,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  }

  const currentGen = generations.find((g) => g.id === selectedGen);

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* screening failed dialog */}
      <Dialog open={stage === "screen-fail"} onOpenChange={(o) => !o && (setStage("upload"), setConsent(false))}>
        <DialogContent className="rounded-3xl" data-testid="screening-failed-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#4A235A]">
              {convertible ? "This looks like a photo" : "We can't use this image"}
            </DialogTitle>
            <DialogDescription className="text-base text-[#5C4A61]">
              {convertible
                ? "Our magic works on artwork — but no problem! We can turn your photo of a person, animal or pet into the art style you choose first, then transform that into a fantasy character. Or upload an original drawing instead. Uploading is always free."
                : "We can only create a legend from an original drawing, or a photo of a real person, animal or pet. Please upload one of those instead — uploading is always free."}
            </DialogDescription>
          </DialogHeader>
          <p className="rounded-2xl bg-[#4A235A]/5 p-3 text-sm font-medium text-[#5C4A61]">{failReason}</p>
          {convertible && (
            <>
              <div data-testid="medium-selector">
                <p className="mb-2 text-sm font-bold text-[#4A235A]">Choose your art style</p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {MEDIA_OPTIONS.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setMedium(m.id)}
                      data-testid={`medium-${m.id}`}
                      className={`group overflow-hidden rounded-2xl border-2 text-left transition-transform hover:-translate-y-0.5 ${
                        medium === m.id ? "border-[#F4D03F] ring-2 ring-[#F4D03F]/40" : "border-[#4A235A]/10"
                      }`}
                    >
                      <img src={m.thumb} alt={m.label} loading="lazy" className="h-16 w-full object-cover" />
                      <span className={`block px-2 py-1.5 text-xs font-bold ${medium === m.id ? "bg-[#9B5DE5]/10 text-[#4A235A]" : "bg-white text-[#5C4A61]"}`}>
                        {m.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <label
                data-testid="photo-consent-label"
                className="mt-2 flex cursor-pointer items-start gap-3 rounded-2xl border border-[#4A235A]/15 bg-white p-3 text-sm font-medium text-[#5C4A61]"
              >
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="photo-consent-checkbox"
                  className="mt-0.5 h-5 w-5 shrink-0 accent-[#9B5DE5]"
                />
                <span>I confirm I have permission to use this photo (it's mine, or of my own child/pet, or I have the owner's consent).</span>
              </label>
              <button
                data-testid="convert-to-sketch-button"
                onClick={() => convertToSketch("")}
                disabled={converting || !consent}
                className="mt-2 min-h-[52px] rounded-full bg-[#F4D03F] font-extrabold text-[#1A0F1C] transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {converting ? `Creating your ${MEDIA_LABELS[medium]}…` : `✨ Create my ${MEDIA_LABELS[medium]} & continue`}
              </button>
            </>
          )}
          <button
            data-testid="reupload-button"
            onClick={() => {
              setStage("upload");
              setUploadPreview(null);
              setConvertible(false);
              setConsent(false);
            }}
            className="mt-2 min-h-[52px] rounded-full border border-[#4A235A]/20 font-bold text-[#4A235A] transition-transform hover:-translate-y-0.5"
          >
            Upload a different image
          </button>
        </DialogContent>
      </Dialog>

      <div className="mx-auto max-w-5xl px-5 py-8 sm:px-8">
        <StepBar stage={stage} />

        <AnimatePresence mode="wait">
          {/* UPLOAD */}
          {stage === "upload" && (
            <motion.div key="upload" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <h1 className="font-display text-3xl font-black text-[#1A0F1C] sm:text-4xl">Upload your image</h1>
              <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-[#5C4A61]">
                <ShieldCheck size={16} className="text-[#9B5DE5]" /> {cfg.trust_drawings}
              </p>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleFile(e.dataTransfer.files?.[0]);
                }}
                data-testid="upload-dropzone"
                className="mt-6 grid cursor-pointer place-items-center rounded-3xl border-2 border-dashed border-[#9B5DE5]/40 bg-white p-10 text-center transition-colors hover:border-[#9B5DE5]"
              >
                {uploadPreview ? (
                  <img src={uploadPreview} alt="upload preview" className="max-h-64 rounded-2xl object-contain" />
                ) : (
                  <>
                    <span className="grid h-16 w-16 place-items-center rounded-full bg-[#4A235A]/10 text-[#9B5DE5]">
                      <ImagePlus size={30} />
                    </span>
                    <p className="mt-4 font-display text-xl font-bold text-[#4A235A]">Tap to choose an image</p>
                    <p className="mt-1 text-sm font-medium text-[#5C4A61]">or drag & drop • PNG or JPEG • up to 12MB</p>
                  </>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="upload-file-input"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </div>
              <button
                disabled={busy}
                onClick={() => fileRef.current?.click()}
                data-testid="upload-drawing-button"
                className="mt-6 inline-flex min-h-[56px] w-full items-center justify-center gap-2 rounded-full bg-[#F4D03F] px-8 text-base font-extrabold text-[#1A0F1C] transition-transform hover:-translate-y-1 disabled:opacity-60 sm:w-auto"
              >
                <Upload size={20} /> {busy ? "Checking your image…" : "Choose image"}
              </button>
            </motion.div>
          )}

          {/* MEDIUM REVIEW */}
          {stage === "sketch-review" && sketchPreview && (
            <motion.div key="sketch" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="sketch-review-stage">
              <h1 className="font-display text-3xl font-black text-[#1A0F1C] sm:text-4xl">Review your {MEDIA_LABELS[medium]}</h1>
              <p className="mt-2 text-sm font-semibold text-[#5C4A61]">
                We turned your photo into a super-realistic {MEDIA_LABELS[medium]}. Fix anything we got wrong, tweak the style, then keep it or regenerate.
              </p>

              <div className="mt-6 grid gap-8 lg:grid-cols-2">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-[#9B5DE5]/20 to-[#F4D03F]/20 blur-xl" />
                  <img
                    src={sketchPreview}
                    alt={`${MEDIA_LABELS[medium]} preview`}
                    data-testid="sketch-preview-image"
                    className="relative w-full rounded-3xl bg-white object-contain shadow-[0_20px_50px_rgba(74,35,90,0.2)]"
                  />
                </div>

                <div>
                  {/* editable detected traits */}
                  <p className="text-sm font-bold text-[#4A235A]">We detected these details — correct any that are wrong</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {(subjectKind === "animal" ? ANIMAL_TRAIT_FIELDS : PERSON_TRAIT_FIELDS).map((f) => (
                      <div key={f.key} className={f.key === "facial_features" || f.key === "notable_features" ? "sm:col-span-2" : ""}>
                        <label className="text-xs font-bold text-[#5C4A61]">{f.label}</label>
                        <input
                          value={(traits && traits[f.key]) || ""}
                          onChange={(e) => setTraits({ ...(traits || {}), [f.key]: e.target.value })}
                          data-testid={`trait-${f.key}`}
                          className="mt-1 w-full rounded-xl border border-[#4A235A]/15 bg-white px-4 py-2.5 text-sm font-medium outline-none focus:border-[#9B5DE5] focus:ring-2 focus:ring-[#F4D03F]/40"
                        />
                      </div>
                    ))}
                  </div>

                  {/* art style */}
                  <p className="mt-5 text-sm font-bold text-[#4A235A]">Art style</p>
                  <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {MEDIA_OPTIONS.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setMedium(m.id)}
                        data-testid={`review-medium-${m.id}`}
                        className={`group overflow-hidden rounded-2xl border-2 text-left transition-transform hover:-translate-y-0.5 ${
                          medium === m.id ? "border-[#F4D03F] ring-2 ring-[#F4D03F]/40" : "border-[#4A235A]/10"
                        }`}
                      >
                        <img src={m.thumb} alt={m.label} loading="lazy" className="h-14 w-full object-cover" />
                        <span className={`block px-2 py-1 text-[11px] font-bold ${medium === m.id ? "bg-[#9B5DE5]/10 text-[#4A235A]" : "bg-white text-[#5C4A61]"}`}>
                          {m.label}
                        </span>
                      </button>
                    ))}
                  </div>

                  <label className="mt-5 block text-sm font-bold text-[#4A235A]">Want to tweak it? (optional)</label>
                  <input
                    value={sketchHint}
                    onChange={(e) => setSketchHint(e.target.value)}
                    placeholder="e.g. make it more detailed, add a smile, softer shading"
                    data-testid="sketch-hint-input"
                    className="mt-2 w-full rounded-full border border-[#4A235A]/15 bg-white px-6 py-3.5 text-base font-medium outline-none focus:border-[#9B5DE5] focus:ring-4 focus:ring-[#F4D03F]/40"
                  />
                  <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                    <button
                      onClick={() => convertToSketch(sketchHint, traits)}
                      disabled={converting || cooldown || rendersLeft === 0}
                      data-testid="regenerate-sketch-button"
                      className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-[#4A235A]/20 px-6 font-bold text-[#4A235A] transition-colors hover:bg-[#4A235A] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <RefreshCw size={18} className={converting ? "animate-spin" : ""} />
                      {converting
                        ? "Redrawing…"
                        : rendersLeft === 0
                          ? "No regenerations left"
                          : rendersLeft !== null
                            ? `Regenerate (${rendersLeft} left)`
                            : "Regenerate"}
                    </button>
                    <button
                      onClick={() => setStage("questionnaire")}
                      disabled={converting}
                      data-testid="use-sketch-button"
                      className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-[#F4D03F] px-8 font-extrabold text-[#1A0F1C] transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                    >
                      <Wand2 size={18} /> Use this → continue
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* QUESTIONNAIRE */}
          {stage === "questionnaire" && (
            <motion.div key="quest" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <CharacterBuilder
                value={q}
                setValue={setQ}
                isPet={subjectKind === "animal"}
                stepLabel={`Step ${STAGE_TO_STEP.questionnaire + 1} of ${WIZARD_STEPS.length} • Character Details`}
                onBack={() => setStage(generations.length ? "preview" : sketchPreview ? "sketch-review" : "upload")}
                onContinue={submitQuestionnaire}
                submitting={busy}
              />
            </motion.div>
          )}

          {/* GENERATING */}
          {stage === "generating" && (
            <motion.div
              key="gen"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative grid place-items-center py-20"
              data-testid="generating-state"
            >
              <Stardust count={40} />
              <div className="animate-glow-pulse grid h-40 w-40 place-items-center rounded-full bg-gradient-to-tr from-[#4A235A] to-[#9B5DE5]">
                <Wand2 size={56} className="text-[#F4D03F]" />
              </div>
              <h2 className="mt-8 font-display text-2xl font-bold text-[#4A235A]">Weaving the magic…</h2>
              <p className="mt-2 max-w-sm text-center text-sm font-medium text-[#5C4A61]">
                Our artists are sprinkling stardust on {q.name && q.name !== "Name It for Me" ? q.name : "your hero"}. This can take up to a minute.
              </p>
            </motion.div>
          )}

          {/* PREVIEW */}
          {stage === "preview" && currentGen && (
            <motion.div key="prev" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <h1 className="font-display text-3xl font-black text-[#1A0F1C] sm:text-4xl">Meet {currentGen.name || (q.name && q.name !== "Name It for Me" ? q.name : "your legend")}</h1>
              <p className="mt-2 text-sm font-semibold text-[#5C4A61]">
                Preview {used} of {cfg.preview_cap} used • {remaining} free {remaining === 1 ? "try" : "tries"} left
              </p>

              <div className="mt-6 grid gap-8 lg:grid-cols-2">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-[#9B5DE5]/20 to-[#F4D03F]/20 blur-xl" />
                  <img
                    src={`data:image/png;base64,${currentGen.preview_b64}`}
                    alt={q.name}
                    data-testid="preview-image"
                    className="relative w-full rounded-3xl object-contain shadow-[0_20px_50px_rgba(74,35,90,0.2)]"
                  />
                  <span className="absolute right-4 top-4 rounded-full bg-[#1A0F1C]/70 px-3 py-1 text-xs font-bold text-white backdrop-blur">
                    Watermarked preview
                  </span>
                </div>

                <div>
                  {currentGen.story?.tagline && (
                    <p className="font-display text-xl font-bold italic text-[#9B5DE5]">“{currentGen.story.tagline}”</p>
                  )}
                  {currentGen.story?.backstory && (
                    <p className="mt-3 whitespace-pre-line text-sm font-medium leading-relaxed text-[#5C4A61]">
                      {currentGen.story.backstory}
                    </p>
                  )}
                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      onClick={tryAgain}
                      disabled={remaining <= 0}
                      data-testid="try-again-button"
                      className="inline-flex min-h-[52px] items-center gap-2 rounded-full border border-[#4A235A]/20 px-6 font-bold text-[#4A235A] transition-colors hover:bg-[#4A235A] hover:text-white disabled:opacity-40"
                    >
                      <RefreshCw size={18} /> Try again ({remaining} left)
                    </button>
                  </div>

                  {generations.length > 1 && (
                    <div className="mt-6">
                      <p className="mb-2 text-sm font-bold text-[#4A235A]">Your tries</p>
                      <div className="flex flex-wrap gap-3">
                        {generations.map((g) => (
                          <button
                            key={g.id}
                            onClick={() => setSelectedGen(g.id)}
                            data-testid={`gen-thumb-${g.id}`}
                            className={`h-20 w-20 overflow-hidden rounded-2xl border-2 transition-transform hover:-translate-y-0.5 ${
                              g.id === selectedGen ? "border-[#F4D03F] ring-2 ring-[#F4D03F]/50" : "border-transparent"
                            }`}
                          >
                            <img src={`data:image/png;base64,${g.preview_b64}`} alt="try" className="h-full w-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Unlock / packages */}
              <div className="mt-12 rounded-3xl border border-[#4A235A]/10 bg-white/70 p-6 backdrop-blur sm:p-8">
                <h2 className="font-display text-2xl font-bold text-[#1A0F1C]">Love it? Unlock your download</h2>
                {isAdmin ? (
                  <p className="mt-1 flex items-center gap-2 text-sm font-bold text-[#9B5DE5]" data-testid="admin-mode-banner">
                    <Lock size={16} /> Admin mode — every package downloads free for you.
                  </p>
                ) : (
                  <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-[#5C4A61]">
                    <Heart size={16} className="text-[#9B5DE5]" /> {cfg.trust_download}
                  </p>
                )}
                <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                  {cfg.packages.map((p) => (
                    <PackageCard key={p.id} pkg={p} selected={selectedPkg?.id === p.id} onSelect={setSelectedPkg} compact />
                  ))}
                </div>
                {selectedPkg && (
                  <div className="mt-6 rounded-2xl bg-[#4A235A]/5 p-4" data-testid="selected-package-summary">
                    <p className="text-sm font-bold text-[#4A235A]">
                      Selected: {selectedPkg.name} — <span data-testid="selected-package-price">${Number(selectedPkg.price).toFixed(0)}</span>
                    </p>
                    <ul className="mt-1 text-xs font-medium text-[#5C4A61]">
                      {(selectedPkg.features || []).map((f) => <li key={f}>• {f}</li>)}
                    </ul>
                  </div>
                )}
                {!isAdmin && (
                  <label
                    data-testid="rights-consent-label"
                    className="mt-6 flex cursor-pointer items-start gap-3 rounded-2xl border border-[#4A235A]/15 bg-white p-3 text-sm font-medium text-[#5C4A61]"
                  >
                    <input
                      type="checkbox"
                      checked={rightsConfirmed}
                      onChange={(e) => setRightsConfirmed(e.target.checked)}
                      data-testid="rights-consent-checkbox"
                      className="mt-0.5 h-5 w-5 shrink-0 accent-[#9B5DE5]"
                    />
                    <span>
                      I own this image or have permission to upload and transform it, including permission from any
                      identifiable people or a parent/guardian where applicable.{" "}
                      <a href="/examples/upload-rights.html" target="_blank" rel="noreferrer" className="font-bold text-[#9B5DE5] underline">
                        Upload Rights Policy
                      </a>
                    </span>
                  </label>
                )}
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                  {!isAdmin && (
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com — for your download link"
                      data-testid="checkout-email-input"
                      className="w-full rounded-full border border-[#4A235A]/15 bg-white px-6 py-4 text-base font-medium outline-none focus:border-[#9B5DE5] focus:ring-4 focus:ring-[#F4D03F]/40 sm:max-w-xs"
                    />
                  )}
                  <button
                    onClick={pay}
                    disabled={busy || (!isAdmin && !rightsConfirmed)}
                    data-testid="pay-download-button"
                    className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-full bg-[#F4D03F] px-8 text-base font-extrabold text-[#1A0F1C] transition-transform hover:-translate-y-1 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Lock size={18} />{" "}
                    {isAdmin
                      ? selectedPkg
                        ? `Download ${selectedPkg.name} free`
                        : "Download free (admin)"
                      : selectedPkg
                        ? `Pay $${Number(selectedPkg.price).toFixed(0)} & download`
                        : "Pay to download"}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function StepBar({ stage }) {
  const steps = WIZARD_STEPS;
  const idx = STAGE_TO_STEP[stage] ?? 0;
  return (
    <div className="mb-8 flex items-center gap-2">
      {steps.map((s, i) => (
        <div key={s} className="flex flex-1 items-center gap-2">
          <div
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-extrabold ${
              i <= idx ? "bg-[#4A235A] text-white" : "bg-[#4A235A]/10 text-[#5C4A61]"
            }`}
          >
            {i + 1}
          </div>
          <span className={`hidden text-xs font-bold sm:block ${i <= idx ? "text-[#4A235A]" : "text-[#5C4A61]"}`}>{s}</span>
          {i < steps.length - 1 && <div className={`h-0.5 flex-1 rounded ${i < idx ? "bg-[#4A235A]" : "bg-[#4A235A]/10"}`} />}
        </div>
      ))}
    </div>
  );
}
